/**
 * Pestaña Resumen: franja con las 4 cifras clave + franja compacta con 4 secundarias,
 * gráfico de cash flow a 12 meses, lista «Para revisar», comparativa de propiedades y renta
 * media por habitación.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, queryKeys } from '../../api/client';
import { Card, CardHeader } from '../../components/Card';
import { KpiCard, KpiStrip, StatCard } from '../../components/KpiCard';
import { ProgressBar } from '../../components/ProgressBar';
import { Thumbnail } from '../../components/Avatar';
import { CashflowLegend, StackedBarChart } from '../../components/StackedBarChart';
import { ErrorState } from '../../components/EmptyState';
import { SkeletonCard, SkeletonKpiRow } from '../../components/Skeleton';
import {
  formatEur,
  formatNumber,
  formatPercent,
  formatTrend,
  formatTrendAmount,
} from '../../lib/format';
import styles from '../DashboardPage.module.css';

export function SummaryTab() {
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.dashboardSummary,
    queryFn: api.dashboardSummary,
  });

  if (isPending) {
    return (
      <>
        <SkeletonKpiRow count={4} />
        <SkeletonKpiRow count={4} />
        <div className={styles.gridChart}>
          <SkeletonCard rows={1} height={190} />
          <SkeletonCard rows={3} height={56} />
        </div>
      </>
    );
  }

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const { kpis, cashflow, comparison, rentPerBedroom, insights } = data;

  return (
    <>
      {/* --- Lo importante, en grande: una franja con las 4 cifras clave ---- */}
      <KpiStrip data-tour="kpis">
        <KpiCard
          label="Valor de la cartera"
          value={formatEur(kpis.portfolioValue)}
          trend={`${formatTrend(kpis.portfolioValueTrend.percent)} este año`}
          trendDirection={kpis.portfolioValueTrend.direction}
        />
        <KpiCard
          label="Retorno del activo"
          value={formatPercent(kpis.assetReturn)}
          trend={formatTrendAmount(kpis.assetReturnGain)}
          trendDirection={Number(kpis.assetReturnGain) >= 0 ? 'up' : 'down'}
        />
        <KpiCard
          label="Ocupación"
          value={formatPercent(kpis.occupancyRate, 0)}
          pill={
            kpis.occupancyRate >= 100
              ? 'Totalmente ocupado'
              : `${kpis.occupiedUnits} de ${kpis.totalUnits} habitaciones`
          }
        />
        <KpiCard
          label="Ingresos mensuales"
          value={formatEur(kpis.monthlyIncome)}
          note="Renta bruta"
        />
      </KpiStrip>

      {/* --- Cifras secundarias: franja compacta --------------------------- */}
      <KpiStrip compact last>
        <StatCard label="Cash flow neto" value={formatEur(kpis.netCashFlow)} unit=" /mes" />
        <StatCard label="Deuda pendiente" value={formatEur(kpis.outstandingDebt)} />
        <StatCard label="Propiedades" value={formatNumber(kpis.propertyCount)} />
        <StatCard label="Habitaciones" value={formatNumber(kpis.unitCount)} />
      </KpiStrip>

      {/* --- Gráfico + insights ------------------------------------------- */}
      <div className={styles.gridChart}>
        <Card>
          <CardHeader title="Cash flow neto por mes" aside={<CashflowLegend />} />
          <StackedBarChart data={cashflow} />
        </Card>

        <Card className={styles.insightsPanel}>
          <CardHeader title="Para revisar" />
          <div className={styles.insightsList}>
            {insights.length === 0 ? (
              <p className={styles.insightsEmpty}>Nada pendiente por ahora.</p>
            ) : null}
            {insights.map((insight) => (
              <div key={insight.id} className={styles.insight}>
                <div className={styles.insightTitle}>{insight.title}</div>
                <div className={styles.insightBody}>{insight.body}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* --- Comparativa + renta media ------------------------------------ */}
      <div className={styles.gridChartLast}>
        <Card>
          <CardHeader title="Comparar propiedades" />
          {comparison.map((row) => (
            <button
              key={row.id}
              type="button"
              className={styles.compareRow}
              onClick={() => navigate(`/propiedades/${row.id}`)}
            >
              <Thumbnail size={40} radius={6} />
              <div className={styles.compareName}>
                <div className={styles.compareNameMain}>{row.name}</div>
                <div className={styles.compareNameSub}>{row.city}</div>
              </div>
              <ProgressBar
                percent={row.barPct}
                className={styles.compareBar}
                label={`Rentabilidad de ${row.name}`}
              />
              <div className={styles.compareValue}>{formatPercent(row.grossYield)}</div>
            </button>
          ))}
        </Card>

        <Card>
          <CardHeader
            title="Renta media por habitación"
            subtitle="Ingreso mensual medio por habitación alquilada"
          />
          <div className={styles.rentHero}>{formatEur(rentPerBedroom.average)}</div>
          <div
            className={
              rentPerBedroom.trend.direction === 'flat'
                ? `${styles.rentTrend} ${styles.rentTrendFlat}`
                : styles.rentTrend
            }
          >
            {rentPerBedroom.trend.percent === null
              ? 'Sin histórico comparable'
              : rentPerBedroom.trend.percent === 0
                ? 'Sin variación vs. trimestre anterior'
                : `${formatTrend(rentPerBedroom.trend.percent)} vs. trimestre anterior`}
          </div>
          <div className={styles.rentBands}>
            {rentPerBedroom.bands.map((band) => (
              <div key={band.bedrooms} className={styles.rentBand}>
                <span className={styles.rentBandLabel}>{band.label}</span>
                <span className={styles.rentBandValue}>{formatEur(band.averageRent)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
